import React, { FunctionComponent } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

import { fetch_all_with_count, fetch_one } from '../../../../lib/v31lib';
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Box,
  Divider,
  Grid,
  Paper,
  Skeleton,
} from '@mui/material';

import {
  Typography
} from '../../../../components/v2/styled';

const { DateTime } = require("luxon");

import HelpersPractice from '../../../../actions/helpers/practice';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

import { User } from '../../../../models/User';
import { Appointment } from '../../../../models/Appointment';
import { Medication } from '../../../../models/Medication';
import { NutriMail } from '../../../../models/NutriMail';
import { EhealthMail } from '../../../../models/EhealthMail';
import { ZorgMail } from '../../../../models/ZorgMail';
import { MollieLink } from '../../../../models/MollieLink';
import { KnowledgeSubscription } from '../../../../models/KnowledgeSubscription';
import { QuestionnaireResult } from '../../../../models/QuestionnaireResult';
import { DiaryDay } from '../../../../models/DiaryDay';
import { MoveMoment } from '../../../../models/MoveMoment';
import { Advice } from '../../../../models/Advice';
import { TreatmentPlan } from '../../../../models/TreatmentPlan';

type Props = {
  object: User;
}

// Eén knoop op de tijdlijn. `day` is de sorteerdatum (ISO), `who` de actor.
type StatusNode = {
  key: string;
  id: string;
  day: string;
  who?: string;
};

// Lokale datumformatter (Dt-identiek): DDD = volledige dagtekst.
const fmtDayFull = (iso?: string, locale?: string) => {
  if (!iso) return '';
  return DateTime.fromISO(iso).setZone("Europe/Brussels").toFormat("DDD", {locale: locale || 'nl'});
};

export const CoachUserWidgetsStatus: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);

  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.statusValueKey];
  const amount = HelpersClientWidgets.getStatusAmount(cfg);
  const itemsMap = cfg?.items || {};

  // Bronnen die actief mogen meedoen (module + consent + coach-vinkje).
  const consentStatus = object?.consent_status;
  const activeItems = HelpersClientWidgets.getOrderedStatusItems(itemsMap, currentPractice, consentStatus)
    .filter((item) => item.state === 'active');
  const activeKeys = new Set(activeItems.map((i) => i.key));
  const isOn = (k: string) => activeKeys.has(k);

  const practiceId = (login?.practice_id || currentPractice?.id);
  const locale = i18n.resolvedLanguage || 'nl';
  const zone = 'Europe/Brussels';

  const queryBase = {
    enabled: !!object?.id && object?.type === "User::User" && !login?.is_secretary,
  };

  // Alle bronqueries. Per bron per_page klein (meestal 1 + count, of het
  // scan-venster voor de diary_day-bronnen). enabled per bronvinkje.
  const results = useQueries({
    queries: [
      // laatste intake client: intake_profile.updated_at
      { ...queryBase, queryKey: ['status_w', 'intake_c', object?.id],
        queryFn: () => fetch_one<any>(`/${locale}/v3/objects/fetch_all`, { object: 'user', id: object?.id, fields: ['id', 'intake_profile'], sub_fields: {intake_profile: ['id', 'created_at', 'updated_at']} }, login),
        enabled: queryBase.enabled && isOn('intake_client') },
      // laatste intake coach: intake_profile_practice.updated_at + editor
      { ...queryBase, queryKey: ['status_w', 'intake_p', object?.id],
        queryFn: () => fetch_one<any>(`/${locale}/v3/objects/fetch_all`, { object: 'user', id: object?.id, fields: ['id', 'intake_profile_practice'], sub_fields: {intake_profile_practice: ['id', 'created_at', 'updated_at', 'editor|id/full_name']}, practice_id: practiceId }, login),
        enabled: queryBase.enabled && isOn('intake_coach') },
      // afspraken: laatste + wie (coach) + betaling (order pipe: id/paid/order_data)
      { ...queryBase, queryKey: ['status_w', 'appointments', object?.id],
        queryFn: () => fetch_all_with_count<Appointment>(`/${locale}/v3/objects/fetch_all`, {
          object: 'appointment', fields: ['id', 'start_time', 'is_cancelled', 'coach|id/full_name', 'order|id/paid/order_data'], sub_fields: {'coach': ['id', 'full_name'], 'order': ['id', 'paid', 'order_data']},
          order: 'start_time DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: object?.id, practice_id: practiceId } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && (isOn('appointment') || isOn('appointment_paid')) },
      // behandeldplan
      { ...queryBase, queryKey: ['status_w', 'tplan', object?.id, practiceId],
        queryFn: () => fetch_all_with_count<TreatmentPlan>(`/${locale}/v3/objects/fetch_all`, {
          object: 'treatment_plan', fields: ['id', 'created_at', 'updated_at', 'feedback_at', 'editor|id/full_name'], sub_fields: {'editor': ['id', 'full_name']},
          order: 'updated_at DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: object?.id, practice_id: practiceId } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && (isOn('treatment_plan') || isOn('treatment_plan_feedback')) },
      // consultaties (advices)
      { ...queryBase, queryKey: ['status_w', 'advice', object?.id, practiceId],
        queryFn: () => fetch_all_with_count<Advice>(`/${locale}/v3/objects/fetch_all`, {
          object: 'advice', fields: ['id', 'created_at', 'advice_date', 'coach_id', 'feedback_at', 'feedback_goals'],
          order: 'advice_date DESC, id DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: object?.id, practice_id: practiceId } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && (isOn('consult_created') || isOn('consult_feedback')) },
      // bijlages (advice_uploads)
      { ...queryBase, queryKey: ['status_w', 'uploads', object?.id, practiceId],
        queryFn: () => fetch_all_with_count<any>(`/${locale}/v3/objects/fetch_all`, {
          object: 'advice_upload', fields: ['id', 'created_at', 'date'],
          order: 'date DESC, created_at DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: object?.id, practice_id: practiceId } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('attachment_added') },
      // tijdlijn (medication)
      { ...queryBase, queryKey: ['status_w', 'meds', object?.id, practiceId],
        queryFn: () => fetch_all_with_count<Medication>(`/${locale}/v3/objects/fetch_all`, {
          object: 'medication', fields: ['id', 'created_at', 'med_start'],
          order: 'created_at DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: object?.id, practice_id: practiceId } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('timeline_filled') },
      // vragenlijsten: uitnodiging/herinnering (mail_sent_at), ontvangen (completed_at), afgerond (is_locked + updated_at)
      { ...queryBase, queryKey: ['status_w', 'qresults', object?.id, practiceId],
        queryFn: () => fetch_all_with_count<QuestionnaireResult>(`/${locale}/v3/objects/fetch_all`, {
          object: 'questionnaire_result', fields: ['id', 'created_at', 'updated_at', 'invited_at', 'mail_sent_at', 'completed_at', 'is_locked', 'questionnaire|id/name'], sub_fields: {'questionnaire': ['id', 'name']},
          order: 'updated_at DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: object?.id, practice_id: practiceId } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && (isOn('questionnaire_created') || isOn('questionnaire_reminder') || isOn('questionnaire_received') || isOn('questionnaire_locked')) },
      // nutrimail ontvangen + verzonden
      { ...queryBase, queryKey: ['status_w', 'nutri_in', object?.id],
        queryFn: () => fetch_all_with_count<NutriMail>(`/${locale}/v3/objects/fetch_all`, {
          object: 'nutri_mail', fields: ['id', 'created_at', 'from', 'to'], sub_fields: {from: ['id', 'full_name'], to: ['id', 'full_name']},
          order: 'created_at DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { to_id: login?.id, from_id: object?.id, sent: 1, deleted: '0', pov: 1 } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('nutrimail_received') },
      { ...queryBase, queryKey: ['status_w', 'nutri_out', object?.id],
        queryFn: () => fetch_all_with_count<NutriMail>(`/${locale}/v3/objects/fetch_all`, {
          object: 'nutri_mail', fields: ['id', 'created_at', 'from', 'to'], sub_fields: {from: ['id', 'full_name'], to: ['id', 'full_name']},
          order: 'created_at DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { from_id: login?.id, to_id: object?.id, sent: 1, deleted: '0' } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('nutrimail_sent') },
      // ehealth
      { ...queryBase, queryKey: ['status_w', 'eh_in', object?.id],
        queryFn: () => fetch_all_with_count<EhealthMail>(`/${locale}/v3/objects/fetch_all`, {
          object: 'ehealth_mail', fields: ['id', 'created_at', 'sender'],
          order: 'id DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { box: 'in', client_id: object?.id } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('ehealth_received') },
      { ...queryBase, queryKey: ['status_w', 'eh_out', object?.id],
        queryFn: () => fetch_all_with_count<EhealthMail>(`/${locale}/v3/objects/fetch_all`, {
          object: 'ehealth_mail', fields: ['id', 'created_at', 'recipients'],
          order: 'id DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { box: 'sent', client_id: object?.id } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('ehealth_sent') },
      // zorgmail
      { ...queryBase, queryKey: ['status_w', 'zm_in', object?.id],
        queryFn: () => fetch_all_with_count<ZorgMail>(`/${locale}/v3/objects/fetch_all`, {
          object: 'zorg_mail', fields: ['id', 'created_at', 'message_data'],
          order: 'created_at DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: login?.id, is_deleted: '0', is_sending: '0', client_id: object?.id } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('zorgmail_received') },
      { ...queryBase, queryKey: ['status_w', 'zm_out', object?.id],
        queryFn: () => fetch_all_with_count<ZorgMail>(`/${locale}/v3/objects/fetch_all`, {
          object: 'zorg_mail', fields: ['id', 'created_at', 'message_data'],
          order: 'created_at DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: login?.id, is_sent: 1, is_deleted: '0', client_id: object?.id } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('zorgmail_sent') },
      // elearning: subscriptions (uitnodiging, status)
      { ...queryBase, queryKey: ['status_w', 'know', object?.id, practiceId],
        queryFn: () => fetch_all_with_count<KnowledgeSubscription>(`/${locale}/v3/objects/fetch_all`, {
          object: 'knowledge_subscription', fields: ['id', 'created_at', 'last_activity', 'finished_at', 'is_approved', 'approved_at', 'knowledge_course'], sub_fields: {'knowledge_course': ['id', 'name']},
          order: 'id DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: object?.id, practice_id: practiceId } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && (isOn('elearning_invite') || isOn('elearning_status')) },
      // elearning betaling: betaalde mollie_link op een subscription
      { ...queryBase, queryKey: ['status_w', 'know_pay', object?.id],
        queryFn: () => fetch_all_with_count<MollieLink>(`/${locale}/v3/objects/fetch_all`, {
          object: 'mollie_link', fields: ['id', 'created_at', 'paid_at', 'description'],
          order: 'id DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { for_client: object?.id, practice_id: practiceId, linked_item_type: 'KnowledgeSubscription', paid: '1' } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('elearning_payment') },
      // betaallinks
      { ...queryBase, queryKey: ['status_w', 'mollie', object?.id],
        queryFn: () => fetch_all_with_count<MollieLink>(`/${locale}/v3/objects/fetch_all`, {
          object: 'mollie_link', fields: ['id', 'created_at', 'paid_at', 'description'],
          order: 'id DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { for_client: object?.id, practice_id: practiceId } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('mollie_link') },
      // betaallinks betaald: enkel de betaalde
      { ...queryBase, queryKey: ['status_w', 'mollie_paid', object?.id],
        queryFn: () => fetch_all_with_count<MollieLink>(`/${locale}/v3/objects/fetch_all`, {
          object: 'mollie_link', fields: ['id', 'created_at', 'paid_at', 'description'],
          order: 'id DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { for_client: object?.id, practice_id: practiceId, paid: '1' } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('mollie_link_paid') },
      // diary scan: eetdagboek + slaap + dagnotities (client note + coach feedbacks)
      { ...queryBase, queryKey: ['status_w', 'diary_days', object?.id],
        queryFn: () => fetch_all_with_count<DiaryDay>(`/${locale}/v3/objects/fetch_all`, {
          object: 'diary_day', fields: ['id', 'day', 'weight', 'wake_time', 'sleep_time', 'client_note', 'hide_practices', 'feedbacks', 'breakfast', 'lunch', 'diner', 'snack1'], sub_fields: {feedbacks: ['id', 'user|id/full_name', 'practice_id', 'comment', 'is_internal', 'created_at']},
          order: 'day DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: object?.id } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && (isOn('eat_diary') || isOn('sleep_times') || isOn('weight_client') || isOn('daynote_client') || isOn('daynote_coach')) },
      // beweegdagboek
      { ...queryBase, queryKey: ['status_w', 'move', object?.id],
        queryFn: () => fetch_all_with_count<MoveMoment>(`/${locale}/v3/objects/fetch_all`, {
          object: 'move_moment', fields: ['id', 'entry_time'],
          order: 'entry_time DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { user_id: object?.id } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && isOn('move_diary') },
      // dagboek-feedbacks: één query, client-side gesplitst op object_type
      // (eat = EatMoment/FoodEntry, move = MoveMoment). De for_user-filter
      // op de backend bekijkt enkel de laatste 6 weken — genoteerd in de
      // handoff, voor een status-tijdlijn is recent volledig genoeg.
      { ...queryBase, queryKey: ['status_w', 'diary_fb', object?.id, practiceId],
        queryFn: () => fetch_all_with_count<any>(`/${locale}/v3/objects/fetch_all`, {
          object: 'diary_feedback', fields: ['id', 'created_at', 'user|id/full_name', 'object_type'], sub_fields: {'user': ['id', 'full_name']},
          order: 'created_at DESC', per_page: amount, page: 0,
          filter: { search: '', advanced: { practice_id: practiceId, for_user: object?.id } }
        }, login), select: (d: any) => ({rows: d.result, count: d.count}),
        enabled: queryBase.enabled && (isOn('eat_diary_feedback') || isOn('move_diary_feedback')) },
      // gewicht: volledige historiek via de weight_stats custom action (breed
      // venster: 10 jaar), client-side gefilterd op dagen met een gewicht.
      // Geeft per ingevuld gewicht een knoop (dd/mm/y namen worden herparsed).
      { ...queryBase, queryKey: ['status_w', 'weight_stats', object?.id],
        queryFn: () => fetch_one<any>(`/${locale}/v3/objects/custom_action`, {
          object: 'user', class_action: 'custom_api_weight_stats', id: object?.id,
          day: DateTime.now().toFormat('y-LL-dd'), range: 3650
        }, login),
        enabled: queryBase.enabled && isOn('weight_client') },
    ],
  });

  // index-hulpjes per bron
  const [intakeC, intakeP, apps, tplan, advice, uploads, meds, qresults, nutriIn, nutriOut, ehIn, ehOut, zmIn, zmOut, know, knowPay, mollie, molliePaid, diaryDays, move, diaryFb, weightStats] = results;
  const q = (r: any) => (r?.isSuccess ? (r.data as {rows: any[]; count: number} | undefined) : undefined);

  // Per bron: welke query-index laadt hij, en welke label-key hoort erbij.
  // loaders zijn een Opsomming van de useQueries-array hierboven (volgorde = bron).
  type SourceLoader = { key: string; loading: boolean };
  const sourceLoaders: SourceLoader[] = [
    // { key: 'intake_client',  loading: !!intakeC?.isLoading },
    // { key: 'intake_coach',   loading: !!intakeP?.isLoading },
    { key: 'appointment',    loading: !!apps?.isLoading },
    { key: 'treatment_plan', loading: !!tplan?.isLoading },
    { key: 'consult_created', loading: !!advice?.isLoading },
    { key: 'attachment_added', loading: !!uploads?.isLoading },
    { key: 'timeline_filled', loading: !!meds?.isLoading },
    { key: 'questionnaire_created', loading: !!qresults?.isLoading },
    { key: 'nutrimail_received', loading: !!nutriIn?.isLoading },
    { key: 'nutrimail_sent', loading: !!nutriOut?.isLoading },
    { key: 'ehealth_received', loading: !!ehIn?.isLoading },
    { key: 'ehealth_sent',    loading: !!ehOut?.isLoading },
    { key: 'zorgmail_received', loading: !!zmIn?.isLoading },
    { key: 'zorgmail_sent',   loading: !!zmOut?.isLoading },
    { key: 'elearning_invite', loading: !!know?.isLoading },
    { key: 'elearning_payment', loading: !!knowPay?.isLoading },
    { key: 'mollie_link',    loading: !!mollie?.isLoading },
    { key: 'mollie_link_paid', loading: !!molliePaid?.isLoading },
    { key: 'eat_diary',      loading: !!diaryDays?.isLoading },
    { key: 'move_diary',     loading: !!move?.isLoading },
    { key: 'eat_diary_feedback', loading: !!diaryFb?.isLoading },
    { key: 'weight_client',  loading: !!weightStats?.isLoading },
  ];

  // Knopen bouwen per bron.
  const nodes: StatusNode[] = [];

  if (isOn('client_created') && !!object?.created_at) {
    nodes.push({ key: 'client_created', id: 'client_created', day: object.created_at });
  }
  const lastLoginAt = [object?.current_sign_in_at, object?.last_sign_in_at].filter(Boolean).sort().pop();
  if (isOn('last_login') && !!lastLoginAt) {
    nodes.push({ key: 'last_login', id: 'last_login', day: lastLoginAt });
  }

  const intakeCData = intakeC?.isSuccess ? (intakeC.data as any) : undefined;
  if (isOn('intake_client') && !!intakeCData?.intake_profile?.updated_at) {
    // nodes.push({ key: 'intake_client', id: 'intake_client', day: intakeCData.intake_profile.updated_at });
  }
  const intakePData = intakeP?.isSuccess ? (intakeP.data as any) : undefined;
  if (isOn('intake_coach') && !!intakePData?.intake_profile_practice?.updated_at) {
    // nodes.push({ key: 'intake_coach', id: 'intake_coach', day: intakePData.intake_profile_practice.updated_at, who: intakePData.intake_profile_practice?.editor_full_name });
  }

  const appsD = q(apps);
  if (appsD && isOn('appointment')) {
    (appsD.rows || []).forEach((a) => {
      if (!a.start_time) return;
      nodes.push({ key: 'appointment', id: `appointment_${a.id}`, day: a.start_time, who: a.coach_full_name });
    });
  }
  if (appsD && isOn('appointment_paid')) {
    (appsD.rows || []).forEach((a) => {
      if (!a.order_paid || a.is_cancelled) return;
      const paidAt = (a as any).order_order_data?.mollie?.paid_at || a.start_time;
      if (!paidAt) return;
      nodes.push({ key: 'appointment_paid', id: `appointment_paid_${a.id}`, day: paidAt });
    });
  }

  const tplanD = q(tplan);
  if (tplanD) {
    (tplanD.rows || []).forEach((row) => {
      if (isOn('treatment_plan') && !!row.updated_at) {
        nodes.push({ key: 'treatment_plan', id: `tplan_${row.id}`, day: row.updated_at, who: (row as any).editor_full_name });
      }
      if (isOn('treatment_plan_feedback') && !!row.feedback_at) {
        nodes.push({ key: 'treatment_plan_feedback', id: `tplan_fb_${row.id}`, day: row.feedback_at as string });
      }
    });
  }

  const adviceD = q(advice);
  if (adviceD) {
    (adviceD.rows || []).forEach((row) => {
      if (isOn('consult_created')) {
        const day = row.created_at || row.advice_date;
        if (!!day) nodes.push({ key: 'consult_created', id: `consult_${row.id}`, day });
      }
      if (isOn('consult_feedback') && !!row.feedback_at) {
        nodes.push({ key: 'consult_feedback', id: `consult_fb_${row.id}`, day: row.feedback_at });
      }
    });
  }

  const uploadsD = q(uploads);
  if (uploadsD && isOn('attachment_added')) {
    (uploadsD.rows || []).forEach((row) => {
      const day = row.date || row.created_at;
      if (!!day) nodes.push({ key: 'attachment_added', id: `upload_${row.id}`, day });
    });
  }

  const medsD = q(meds);
  if (medsD && isOn('timeline_filled')) {
    (medsD.rows || []).forEach((row) => {
      if (!!row.created_at) nodes.push({ key: 'timeline_filled', id: `med_${row.id}`, day: row.created_at });
    });
  }

  const qrD = q(qresults);
  if (qrD) {
    (qrD.rows || []).forEach((row) => {
      if (isOn('questionnaire_created') && !!row.created_at) {
        nodes.push({ key: 'questionnaire_created', id: `qr_${row.id}`, day: row.created_at, who: (row as any).questionnaire?.name });
      }
      if (isOn('questionnaire_reminder') && !!row.mail_sent_at) {
        nodes.push({ key: 'questionnaire_reminder', id: `qrm_${row.id}`, day: row.mail_sent_at });
      }
      if (isOn('questionnaire_received') && !!row.completed_at) {
        nodes.push({ key: 'questionnaire_received', id: `qrc_${row.id}`, day: row.completed_at });
      }
      if (isOn('questionnaire_locked') && !!row.is_locked) {
        nodes.push({ key: 'questionnaire_locked', id: `qrl_${row.id}`, day: row.updated_at });
      }
    });
  }

  const nmIn = q(nutriIn);
  if (nmIn && isOn('nutrimail_received')) {
    (nmIn.rows || []).forEach((row) => {
      if (!!row.created_at) nodes.push({ key: 'nutrimail_received', id: `nmi_${row.id}`, day: row.created_at, who: (row as any).from?.full_name });
    });
  }
  const nmOut = q(nutriOut);
  if (nmOut && isOn('nutrimail_sent')) {
    (nmOut.rows || []).forEach((row) => {
      if (!!row.created_at) nodes.push({ key: 'nutrimail_sent', id: `nmo_${row.id}`, day: row.created_at, who: (row as any).to?.full_name });
    });
  }

  const ehInD = q(ehIn);
  if (ehInD && isOn('ehealth_received')) {
    (ehInD.rows || []).forEach((row) => {
      if (!!row.created_at) nodes.push({ key: 'ehealth_received', id: `ehi_${row.id}`, day: row.created_at });
    });
  }
  const ehOutD = q(ehOut);
  if (ehOutD && isOn('ehealth_sent')) {
    (ehOutD.rows || []).forEach((row) => {
      if (!!row.created_at) nodes.push({ key: 'ehealth_sent', id: `eho_${row.id}`, day: row.created_at });
    });
  }

  const zmInD = q(zmIn);
  if (zmInD && isOn('zorgmail_received')) {
    (zmInD.rows || []).forEach((row) => {
      if (!!row.created_at) nodes.push({ key: 'zorgmail_received', id: `zmi_${row.id}`, day: row.created_at });
    });
  }
  const zmOutD = q(zmOut);
  if (zmOutD && isOn('zorgmail_sent')) {
    (zmOutD.rows || []).forEach((row) => {
      if (!!row.created_at) nodes.push({ key: 'zorgmail_sent', id: `zmo_${row.id}`, day: row.created_at });
    });
  }

  const knowD = q(know);
  if (knowD) {
    (knowD.rows || []).forEach((row) => {
      if (isOn('elearning_invite') && !!row.created_at) {
        nodes.push({ key: 'elearning_invite', id: `kn_${row.id}`, day: row.created_at, who: (row as any).knowledge_course?.name });
      }
      if (isOn('elearning_status')) {
        const day = row.finished_at || row.last_activity;
        if (!!day) nodes.push({ key: 'elearning_status', id: `kns_${row.id}`, day, who: (row as any).knowledge_course?.name });
      }
    });
  }
  const knowPayD = q(knowPay);
  if (knowPayD && isOn('elearning_payment')) {
    (knowPayD.rows || []).forEach((row) => {
      const kp = row as any;
      const day = kp.paid_at || kp.created_at;
      if (!!day) nodes.push({ key: 'elearning_payment', id: `knp_${kp.id}`, day });
    });
  }

  const mollieD = q(mollie);
  if (mollieD && isOn('mollie_link')) {
    (mollieD.rows || []).forEach((row) => {
      if (!!row.created_at) nodes.push({ key: 'mollie_link', id: `ml_${row.id}`, day: row.created_at });
    });
  }
  const molliePaidD = q(molliePaid);
  if (molliePaidD && isOn('mollie_link_paid')) {
    (molliePaidD.rows || []).forEach((row) => {
      const mp = row as any;
      const day = mp.paid_at || mp.created_at;
      if (!!day) nodes.push({ key: 'mollie_link_paid', id: `mlp_${mp.id}`, day });
    });
  }

  const ddD = q(diaryDays);
  if (ddD) {
    const days = ddD.rows || [];
    if (isOn('eat_diary')) {
      days.forEach((d) => {
        if (!!(d as any).breakfast || !!(d as any).lunch || !!(d as any).diner || !!(d as any).snack1) {
          nodes.push({ key: 'eat_diary', id: `eat_${d.id}`, day: `${d.day}T12:00:00` });
        }
      });
    }
    if (isOn('sleep_times')) {
      days.forEach((d) => {
        if (!!d.wake_time || !!d.sleep_time) {
          nodes.push({ key: 'sleep_times', id: `sleep_${d.id}`, day: `${d.day}T12:00:00` });
        }
      });
    }
    if (isOn('daynote_client')) {
      const hidden = ((day: DiaryDay) => (day.hide_practices || '').split(',').map((s) => s.trim()).includes(String(practiceId)));
      days.forEach((d) => {
        if (!!d.client_note && !hidden(d)) {
          nodes.push({ key: 'daynote_client', id: `dnc_${d.id}`, day: `${d.day}T12:00:00` });
        }
      });
    }
    if (isOn('daynote_coach')) {
      (days || []).forEach((d) => {
        (d.feedbacks || []).forEach((f: any) => {
          if (f.practice_id !== practiceId || !f.created_at) return;
          nodes.push({ key: 'daynote_coach', id: `dnco_${f.id}`, day: f.created_at, who: (f as any).user_full_name });
        });
      });
    }
  }
  const moveD = q(move);
  if (moveD && isOn('move_diary')) {
    (moveD.rows || []).forEach((row) => {
      if (!!row.entry_time) nodes.push({ key: 'move_diary', id: `move_${row.id}`, day: row.entry_time });
    });
  }
  const fbD = q(diaryFb);
  if (fbD) {
    (fbD.rows || []).forEach((row) => {
      const ot = (row as any).object_type;
      if (ot === 'Diary::EatMoment' || ot === 'Diary::FoodEntry') {
        if (isOn('eat_diary_feedback') && !!row.created_at) {
          nodes.push({ key: 'eat_diary_feedback', id: `eatfb_${row.id}`, day: row.created_at, who: (row as any).user_full_name });
        }
      } else if (ot === 'Diary::MoveMoment') {
        if (isOn('move_diary_feedback') && !!row.created_at) {
          nodes.push({ key: 'move_diary_feedback', id: `movefb_${row.id}`, day: row.created_at, who: (row as any).user_full_name });
        }
      }
    });
  }
  if (isOn('weight_client') && weightStats?.isSuccess) {
    const points = (weightStats.data as any)?.custom_result?.data || [];
    points.forEach((p: any) => {
      if (!p.weight || !p.name) return;
      // de custom action levert dd/LL/y-namen; naar ISO herparsen voor de sortering
      const dt = DateTime.fromFormat(p.name, 'dd/LL/y', {locale: 'nl'});
      if (dt.isValid) nodes.push({ key: 'weight_client', id: `weight_${p.name}`, day: dt.toISO() });
    });
  }

  // Sorteren: nieuwste eerst, stabiel via key-tiebreak.
  const sorted = nodes
    .filter((n) => !!n.day)
    .sort((a, b) => {
      const aT = DateTime.fromISO(a.day).setZone(zone);
      const bT = DateTime.fromISO(b.day).setZone(zone);
      if (aT.valueOf() !== bT.valueOf()) return bT.valueOf() - aT.valueOf();
      return a.key.localeCompare(b.key);
    })
    .slice(0, amount);

  const loadingSources = sourceLoaders.filter((s) => s.loading && isOn(s.key));
  const loading = loadingSources.length > 0;

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    <Grid container spacing={1} sx={{position: 'relative'}}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.status")}</Typography>
      </Grid>
      <Grid item xs={12}><Divider /></Grid>

      {loading && sorted.length === 0 && <Grid item xs={12} sx={{display: 'flex', flexDirection: 'column', gap: 1, paddingTop: 1}}>
        {Array.from(new Array(3)).map((_, i) => <Box key={i} sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton variant="text" width="80%" />
        </Box>)}
      </Grid>}

      {!loading && sorted.length === 0 && <Grid item xs={12}>
        <Typography variant="body2" sx={{fontStyle: 'italic', color: 'text.secondary'}} html>{t("shared.terms.no_results")}</Typography>
      </Grid>}

      {/* Verticale tijdlijn: bol + dagtekst rechts, actie + wie eronder. */}
      {sorted.map((node, idx) => {
        const item = activeItems.find((i) => i.key === node.key);
        if (!item) return null;
        const { Icon } = item;
        return <React.Fragment key={node.id}>
          <Grid item xs={12} sx={{display: 'flex', gap: 1}}>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '3px'}}>
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
              }} />
              {idx < sorted.length - 1 && <Box sx={{flex: 1, width: '2px', backgroundColor: 'rgba(0,0,0,0.12)', marginTop: '2px'}} />}
            </Box>
            <Grid container spacing={0} sx={{flex: 1, minWidth: 0}}>
              <Grid item xs={12} sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                <Icon sx={{fontSize: '1.1rem', color: 'text.secondary', flexShrink: 0}} />
                <Typography variant="body2" noWrap sx={{flex: 1, minWidth: 0}}>
                  {t(`coach.submenu.detail.status_source.${node.key}`)}
                </Typography>
                <Typography variant="caption" sx={{color: 'text.secondary', flexShrink: 0}}>
                  {fmtDayFull(node.day, locale)}
                </Typography>
              </Grid>
              {!!node.who && <Grid item xs={12}>
                <Typography variant="caption" noWrap sx={{color: 'text.secondary'}}>{node.who}</Typography>
              </Grid>}
            </Grid>
          </Grid>
        </React.Fragment>;
      })}

      {/* Per bron een mini-loader onderaan zolang die bron nog onderweg is:
          dot + bronlabel, verschijnt/verdwijnt onafhankelijk van de andere. */}
      {loadingSources.length > 0 && <Grid item xs={12} sx={{display: 'flex', flexWrap: 'wrap', gap: 1, paddingTop: 1}}>
        {loadingSources.map((s) => <Box key={s.key} sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <Box sx={{
            width: 8, height: 8, borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            animation: 'statusPulse 1.2s ease-in-out infinite',
            '@keyframes statusPulse': {
              '0%, 100%': {opacity: 0.25},
              '50%': {opacity: 1},
            },
          }} />
          <Typography variant="caption" sx={{color: 'text.secondary'}} noWrap>
            {t(`coach.submenu.detail.status_source.${s.key}`)}
          </Typography>
        </Box>)}
      </Grid>}
    </Grid>
  </Paper>;
}
